import axios from 'axios'
import Cookies from 'js-cookie'
import { head, isEmpty, pick, propEq } from 'ramda'
import {
  loadDomains,
  loadDomainTenants,
  loadUserTenants,
} from 'account/components/userManagement/tenants/new-actions'
import {
  getUserPermissions,
  updateSession,
} from 'account/components/userManagement/users/new-actions'
import ApiClient from 'api-client/ApiClient'
import type { GetFeatures } from 'api-client/keystone.model'
import {
  AppPlugins,
  activateUserUrl,
  CustomerTiers,
  DOMAIN_NAME,
  forgotPasswordUrl,
  GlobalPreferences,
  loginUrl,
  loginWithCookieUrl,
  loginWithParams,
  loginWithSsoUrl,
  resetPasswordThroughEmailUrl,
  resetPasswordUrl,
  SSO_REDIRECT_COOKIE,
  ssoEnabledTiers,
} from 'app/constants'
import type { CustomWindow } from 'app/polyfills/window'
import store from 'app/store'
import config from 'app-config'
import { cacheActions } from 'core/caching/cacheReducers'
import { DocumentMetaCls } from 'core/components/DocumentMeta'
import { notificationActions } from 'core/notifications/notificationReducers'
import { parseCookies } from 'core/public/SetCookiePage'
import { permissionsActions } from 'core/session/permissionsReducers'
import { sessionActions } from 'core/session/sessionReducers'
import { getFeaturesJson } from 'core/utils/helpers'
import { isDefaultDomain } from 'core/utils/rbac'
import Watchdog from 'core/watchdog'
import maybeUpdateLogo from 'core/watchdog/maybe-update-logo'
import systemHealthCheck from 'core/watchdog/system-health'
import Bugsnag from 'utils/bugsnag'
import { isNilOrEmpty, pathStrOr } from 'utils/fp'
import { getCookieValue } from 'utils/misc'
import {
  clarityScriptContent,
  injectAmplitudeScriptContent,
  isTrackingEnabled,
  segmentScriptContent,
} from 'utils/tracking'

const urlBase = process.env.NODE_ENV !== 'production' ? config.apiHost : ''
declare let window: CustomWindow

const { preferenceStore, keystone } = ApiClient.getInstance()
const { dispatch } = store

const keysToPluck = [
  'deviceMemory',
  'hardwareConcurrency',
  'appCodeName',
  'appName',
  'appVersion',
  'cookieEnabled',
  'maxTouchPoints',
  'onLine',
  'platform',
  'product',
  'productSub',
  'vendor',
  'vendorSub',
  'webdriver',
]
const whiteListedUrls = [loginUrl, forgotPasswordUrl, resetPasswordUrl, activateUserUrl]
const isWhitelistedUrl = (pathname): boolean => {
  // Allow the following paths to load as entry point when user is not logged in
  return whiteListedUrls.includes(pathname)
}

export const getFeatures = async () => {
  const features: GetFeatures = await getFeaturesJson()

  // const customerTier = pathStrOr(CustomerTiers.Freedom, 'data.customer_tier', features)
  // const sandboxFlag = pathStrOr(false, 'data.experimental.sandbox', features)
  // const analyticsOff = pathStrOr(false, 'data.experimental.analyticsOff', features)
  // const airgapped = pathStrOr(false, 'data.experimental.airgapped', features)
  // const kplane = pathStrOr(false, 'data.experimental.kplane', features)
  // const sso = pathStrOr(false, 'data.experimental.sso', features)
  // const sandbox = pathStrOr(false, 'data.experimental.sandbox', features)
  // const duVersion = pathStrOr('', 'data.releaseVersion', features)
  // const overridePrimaryLogo = pathStrOr('', 'override_primary_logo', features)
  // const overridePrimaryFavicon = pathStrOr('', 'override_primary_favicon', features)
  const productType = pathStrOr('pcd', 'experimental.product_type', features)

  // pluginFeatures
  // const infrastructureOff = pathStrOr(false, 'data.experimental.infrastructureOff', features)
  // const kubernetesOff = pathStrOr(false, 'data.experimental.kubernetesOff', features)
  // const kubevirtOff = pathStrOr(false, 'data.experimental.kubevirtOff', features)
  // const metal3Off = pathStrOr(false, 'data.experimental.metal3Off', features)
  // const accountOff = pathStrOr(false, 'data.experimental.accountOff', features)
  return {
    customerTier: CustomerTiers.Freedom,
    sandboxFlag: false,
    sandbox: false,
    analyticsOff: false,
    airgapped: false,
    kplane: false,
    sso: false,
    duVersion: '',
    overridePrimaryLogo: '',
    overridePrimaryFavicon: '',
    productType,
  }
}

export const loadLoginFeaturesAndTracking = async () => {
  /* eslint-disable */
  // Check for sandbox flag, if false identify the user in Segment using Keystone ID
  // This needs to be done here bc this needs to be done before login.
  // Features are requested again later for the specific logged-in region,
  // whereas this one is done on the master DU from which the UI is served.
  // Ignore exception if features.json not found (for local development)
  const {
    customerTier,
    sandboxFlag,
    analyticsOff,
    airgapped,
    kplane,
    sso,
    duVersion,
    overridePrimaryFavicon,
    overridePrimaryLogo,
  } = await getFeatures()

  Bugsnag.addMetadata('App', {
    customerTier,
    duVersion,
    fqdn: window.location.origin,
  })
  Bugsnag.addMetadata('Device', pick(keysToPluck, window?.navigator || {}))

  // Segment tracking
  // if (!analyticsOff && !airgapped) {
  //   DocumentMetaCls.addScriptElementToDomBody({
  //     id: 'segmentCode',
  //     textContent: segmentScriptContent,
  //   })
  // }

  // Amplitude tracking
  if (!analyticsOff && !airgapped) {
    const trackingEnabled = await isTrackingEnabled()
    if (trackingEnabled) {
      injectAmplitudeScriptContent()
      DocumentMetaCls.addScriptElementToDomBody({
        id: 'clarityCode',
        textContent: clarityScriptContent,
      })
    }
  }

  if (!kplane) {
    // if traditional DU, maybe set custom theme.
    Watchdog.register({
      handler: maybeUpdateLogo(overridePrimaryLogo, overridePrimaryFavicon),
      frequency: 0, // Only set this once
    })
  }
  if (kplane) {
    Watchdog.register({ handler: systemHealthCheck, frequency: 1000 * 60 * 10 })
  }

  // Legacy DU & DDU have different conditions
  return {
    customerTier,
    ssoEnabled: (kplane && ssoEnabledTiers.includes(customerTier)) || (!kplane && sso),
  }
}

const getUserDetails = async (user) => {
  if (typeof window.analytics === 'undefined') return

  window.analytics.identify(user.id, {
    email: user?.email,
    domainId: user?.domain_id,
    defaultProjectId: user?.default_project_id,
    userName: user?.name,
    userId: user?.userId,
    role: user?.role,
  })
}

const AUTH_KEYS = [
  'username',
  'unscopedToken',
  'expiresAt',
  'issuedAt',
  'ssoLogin',
  'domain',
  'currentRegion',
]

export const validateSession = async (getUserPrefs, session, history) => {
  const { pathname, hash, search } = history.location

  // Bypass the session check if we are accessing a whitelisted url
  if (isWhitelistedUrl(pathname) || hash.includes(resetPasswordThroughEmailUrl)) {
    return
  }

  // The auth handoff data for SSO/region-switch logins is carried in the URL
  // fragment (not the query string) so it is never sent to the server or
  // leaked via the Referer header of subsequent requests (PCD-8733). Fall
  // back to the query string so an in-flight redirect issued by an older
  // frontend build (e.g. a peer region mid-upgrade) still logs in correctly.
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const urlParams: any = hashParams.size > 0 ? hashParams : new URLSearchParams(search)

  let authData = null

  // For SSO Login in other Region - User will be redirected to /ui/ssologin with auth data as query params
  // Set the auth data and setup the session
  if ([loginWithParams, loginWithSsoUrl].includes(pathname) && urlParams.size > 0) {
    const unscopedToken = urlParams.get('unscopedToken')
    const ssoLogin = urlParams.get('ssoLogin')

    // Redirect to login page if no unscopedToken is found
    if (unscopedToken === 'undefined' || !unscopedToken) {
      window.location.href = ssoLogin === 'true' ? `${loginUrl}?ssoLoginError=true` : loginUrl
    }

    // Set the unscopedToken in the ApiClient instance
    const client = ApiClient.getInstance()
    client.unscopedToken = unscopedToken

    // Set the authData for the session
    AUTH_KEYS.forEach((key) => {
      const value = urlParams.get(key)
      if (value) {
        authData = { ...authData, [key]: value === 'null' || value === 'undefined' ? null : value }
      }
    })

    // Set the domain cookie
    if (!isDefaultDomain(authData?.domain)) {
      Cookies.remove(DOMAIN_NAME)
      Cookies.set(DOMAIN_NAME, authData?.domain)
    }
    const { features } = session
    const isOpenCloud = features?.experimental?.product_type === 'opencloud'

    // This is needed for opencloud to redirect to the correct region dashboard
    // when the user clicks on a region card in the home page. After logging into a region,
    // we don't want to redirect to the home page (default behavior), but to the
    // region dashboard.
    if (isOpenCloud) {
      const targetPath = urlParams.get('targetPath')
      if (targetPath) {
        localStorage.setItem('targetPath', targetPath)
      }
    }

    // Scrub the auth handoff data from the visible URL immediately, before any
    // of the API calls below fire, so it can never be replayed via the
    // Referer header on those (or any other) subsequent requests (PCD-8733).
    history.replace(pathname)
  }

  if (authData === null) {
    // Validate and "refresh" the existing session
    authData = await restoreSession(pathname, session, history)
    const { issuedAt, username = session.username, unscopedToken, expiresAt, ssoLogin } = authData

    // When logging from a different region - handle the redirect here
    // If redirect cookie is present - redirect to the region url
    if (pathname === loginWithSsoUrl) {
      const cookie = parseCookies(SSO_REDIRECT_COOKIE)
      if (cookie && cookie.redirectUrl) {
        const domain = cookie?.domain || ''

        // Clear all the cookies before redirecting
        Cookies.remove(SSO_REDIRECT_COOKIE)
        Cookies.remove(DOMAIN_NAME)

        // Carry the auth handoff data in the URL fragment, not the query
        // string (PCD-8733) - see redirectToRegionLogin for details.
        const authParams = new URLSearchParams({
          issuedAt: String(issuedAt),
          username: String(username),
          unscopedToken: String(unscopedToken),
          expiresAt: String(expiresAt),
          ssoLogin: 'true',
          domain,
        })
        const url = new URL(cookie.redirectUrl)
        url.hash = authParams.toString()
        window.location.replace(url)
      }
    }
  }

  const { isSsoToken, username } = session

  if (authData?.unscopedToken) {
    const { currentTenant, currentRegion } = getUserPrefs(authData?.username || username)
    await setupSession({
      currentTenant,
      currentRegion: authData?.currentRegion || currentRegion,
      username: authData?.username || username,
      unscopedToken: authData?.unscopedToken,
      expiresAt: authData?.expiresAt,
      issuedAt: authData?.issuedAt,
      isSsoToken: isSsoToken || authData?.ssoLogin === 'true' || authData?.ssoLogin === true,
      domain: !isDefaultDomain(authData?.domain) ? authData?.domain : '',
    })
  } else {
    // Order matters
    dispatch(sessionActions.destroySession())
    dispatch(cacheActions.clearCache())
    dispatch(notificationActions.clearAllNotifications())
    dispatch(permissionsActions.clearPermissions())
  }
}

export const setupSession = async ({
  currentTenant,
  currentRegion,
  username,
  unscopedToken,
  expiresAt,
  issuedAt,
  isSsoToken,
  domain = '',
  password = '',
}) => {
  // Preload domainId if user logs in into specific domain
  const userDomain = domain || Cookies.get(DOMAIN_NAME)
  if (userDomain) {
    await loadDomains(userDomain)
  }

  const tenants = await loadUserTenants()
  if (isNilOrEmpty(tenants)) {
    // Do not return early bc empty tenants is expected for system users
    // Check for if user is a system user or just not assigned any tenants
    // comes in updateSession function below
    console.error('No tenants found for this user')
  }
  const activeTenant =
    tenants.find(propEq('id', currentTenant)) ||
    tenants.find(propEq('name', 'service')) ||
    head(tenants)
  const activeTenantId = activeTenant?.id

  // Order matters
  const user = await updateSession({
    username,
    password,
    unscopedToken,
    expiresAt,
    issuedAt,
    isSsoToken,
    currentTenantId: activeTenantId,
    currentRegion,
    domain,
    activeTenant,
    systemCurrentTenantId: currentTenant, // need to pass this for system user
  })

  try {
    const permissions = await getUserPermissions()
    dispatch(permissionsActions.updatePermissions(permissions))
  } catch (e) {
    console.error(e)
    dispatch(permissionsActions.clearPermissions())
  }

  if (user) {
    await getUserDetails(user)
  }
}

const restoreSession = async (
  pathname,
  session,
  history,
): Promise<{
  username?: string
  unscopedToken?: string
  expiresAt?: string
  issuedAt?: string
  ssoLogin?: boolean
  // eslint-disable-next-line @typescript-eslint/require-await
}> => {
  const { keystone } = ApiClient.getInstance()

  // When trying to login with cookie with pmkft
  if (pathname === loginWithCookieUrl) {
    const urlParams = new URLSearchParams(history.location.search)
    const scopedToken = urlParams.get('token') || getCookieValue('X-Auth-Token')

    // Start from scratch to make use of prebuilt functions
    // for standard login page
    return keystone.getUnscopedTokenWithToken(scopedToken)
  }

  if (pathname === loginWithSsoUrl) {
    return keystone.authenticateSso()
  }

  // Attempt to restore the session
  const { username, unscopedToken: currUnscopedToken } = session
  if (username && currUnscopedToken) {
    // We need to make sure the token has not expired.
    return keystone.renewToken(currUnscopedToken)
  }
  return {}
}

// Load features and update session
// Product type is stored in both localStorage and Redux for different purposes:
// 1. localStorage: Used for early access before Redux store is initialized, particularly
//    by route registration which happens during module imports. This ensures routes
//    have access to the correct product type even before the app is fully bootstrapped.
// 2. Redux store: Used as the source of truth when the app is running.
// The isOpenCloud() helper function checks both sources, prioritizing localStorage
// for early access and Redux store for runtime state.
export const loadFeatures = async (): Promise<GetFeatures> => {
  const defaultProductType = 'pcd'
  const features: GetFeatures = await getFeaturesJson()
  const productType = pathStrOr(defaultProductType, 'experimental.product_type', features)

  localStorage.setItem('productType', productType)
  dispatch(
    sessionActions.updateSession({
      features,
    }),
  )
  return features
}
